import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import { settingsApi } from '../../api/settings'
import type { TenantSettings } from '../../api/types'
import { Alert } from '../../components/Alert'
import { ErrorState, LoadingState } from '../../components/States'
import { useAsync } from '../../hooks/useAsync'
import { SettingsGeneralTab } from './SettingsGeneralTab'
import { SettingsImportTab } from './SettingsImportTab'
import { SettingsItemsTab } from './SettingsItemsTab'
import { SettingsMappingTab } from './SettingsMappingTab'
import { SettingsValidationTab } from './SettingsValidationTab'
import { SettingsFormContext } from './useSettingsForm'
import { notifySettingsSaved } from './settingsEvents'

/* Settings, as a dialog rather than a page.

   It is opened from inside a wizard — the Settings button on the upload and
   mapping steps — and as a page it navigated away from the import the user was
   half-way through, then made them find their way back. A dialog keeps the
   wizard mounted underneath, so closing it returns to exactly the step and
   file that were already loaded.

   All five tabs share one fetch and one Save, because they edit one object:
   switching tabs must not discard edits, and there is only ever one thing to
   persist.

   The footer is pinned rather than at the end of the scrolling body. As a page
   the Save button sat ~1,300px down and was easy to miss; in a dialog whose
   body scrolls, a footer that scrolled away would be the same bug again. */

type TabId = 'general' | 'import' | 'mapping' | 'validation' | 'items'

const TABS: { id: TabId; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'import', label: 'Import' },
  { id: 'mapping', label: 'Mapping' },
  { id: 'validation', label: 'Validation' },
  { id: 'items', label: 'Items' },
]

export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<TabId>('general')
  const loaded = useAsync<TenantSettings>(() => settingsApi.get(), [])
  const [draft, setDraft] = useState<TenantSettings | null>(null)
  const [baseline, setBaseline] = useState<TenantSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  /* Seeding is keyed on the fetched object's identity rather than done in an
     effect, which oxlint rightly flags. `seededFrom` deliberately tracks the
     fetch and not the save: pointing it at the saved value made this re-seed
     from the older fetch and silently undo the save. */
  const [seededFrom, setSeededFrom] = useState<TenantSettings | null>(null)
  if (loaded.data && loaded.data !== seededFrom) {
    setSeededFrom(loaded.data)
    setDraft(loaded.data)
    setBaseline(loaded.data)
  }

  // Escape closes, matching the rest of the app's dialogs.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  /* Closing discards unsaved edits.
   *
   * Without this a previous session's unsaved changes survived into the next
   * one: the controls showed values the server did not hold, and anything
   * derived from the draft — the Mapping tab's "this toggle is blocked"
   * warning, for one — reported on a state the account was not in. Cancel
   * already means "revert"; closing the dialog means the same thing. */
  useEffect(() => {
    if (open) return
    // oxlint-disable-next-line react/set-state-in-effect
    setSaved(false)
    // oxlint-disable-next-line react/set-state-in-effect
    setError(null)
    // oxlint-disable-next-line react/set-state-in-effect
    setDraft(baseline)
  }, [open, baseline])

  if (!open) return null

  const body = () => {
    if (loaded.loading && !draft) return <LoadingState label="Loading preferences…" />
    if (loaded.error && !draft) {
      return <ErrorState error={loaded.error} onRetry={loaded.reload} />
    }
    if (!draft) return null

    switch (tab) {
      case 'import':
        return <SettingsImportTab />
      case 'mapping':
        return <SettingsMappingTab />
      case 'validation':
        return <SettingsValidationTab />
      case 'items':
        return <SettingsItemsTab />
      default:
        return <SettingsGeneralTab />
    }
  }

  const set = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev))
    setSaved(false)
  }

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setError(null)
    try {
      const next = await settingsApi.update(draft)
      setDraft(next)
      setBaseline(next)
      setSaved(true)
      // Only on success: a failed save changed nothing, so invalidating the
      // import preview would throw away a report that is still accurate.
      notifySettingsSaved()
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.')
    } finally {
      setSaving(false)
    }
  }

  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(baseline)

  return (
    <div
      className="scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        /* `bb` carries the design tokens and `bb-settings-dialog` the layout.
           The dialog lives in AppShell, outside the page body where `bb-page`
           sets the tokens — without `bb` here every --bb-* value is undefined,
           so headings fall back to the serif body font and the green button
           draws transparent. */
        className="bb modal wide bb-settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <div className="modal-h">
          <h3>Settings</h3>
          <span className="spacer" />
          <button className="xbtn" type="button" aria-label="Close settings" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-b">
          {/* No intro line here: the footer already says these apply to every
              import in the workspace, and saying it twice cost a row of height
              the tallest tab needed to avoid scrolling. */}
          <div className="bb-tabs">
            {TABS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={entry.id === tab ? 'bb-tab bb-tab-on' : 'bb-tab'}
                onClick={() => setTab(entry.id)}
              >
                {entry.label}
              </button>
            ))}
          </div>

          {error ? (
            <Alert tone="err" title="Could not save your settings">
              {error}
            </Alert>
          ) : null}
          {saved ? <Alert tone="ok" title="Settings saved successfully" /> : null}

          {draft ? (
            <SettingsFormContext.Provider
              value={{ draft, set, goToTab: (next) => setTab(next) }}
            >
              {body()}
            </SettingsFormContext.Provider>
          ) : (
            body()
          )}
        </div>

        {/* Pinned, not scrolled: the body is the scroll container, so a footer
            inside it would move out of reach exactly as the page version did. */}
        {draft ? (
          <div className="modal-f bb-settings-dialog-f">
            <span className="bb-map-help">
              These settings apply to every import in this workspace, including imports using saved
              mappings.
            </span>
            <span className="spacer" />
            {/* Cancel closes the dialog, discarding whatever was edited.
                It used to be `disabled={!dirty}`, which made it unusable in the
                one state a user most expects it to work — nothing changed yet —
                and, when it was enabled, only reverted the draft without
                closing. "Cancel" on a dialog means "leave without saving", and
                the close-on-discard effect above already reverts on close, so
                closing is the whole job. Disabled only while a save is in
                flight, since the dialog must not vanish mid-request. */}
            <button
              className="bb-btn bb-btn-quiet"
              type="button"
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="bb-btn bb-btn-green"
              type="button"
              disabled={!dirty || saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
